import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  LinearProgress,
  Divider,
  Grid,
  Avatar,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as ProgressIcon,
  CheckCircle as CheckIcon,
  PaidRounded as PaidIcon
} from '@mui/icons-material';
import { tokens, gradientForName } from '../../../theme/tokens';

const translations = {
  spanish: {
    draft: 'Borrador', initiated: 'Iniciado', processing: 'En trámite', completed: 'Finalizado',
    client: 'Cliente', lawyer: 'Abogado', notAssigned: 'No asignado',
    start: 'Inicio', end: 'Fin', created: 'Creado', progress: 'Progreso', fees: 'Honorarios',
    completedCase: 'Expediente completado', tasksCompleted: 'tareas finalizadas',
    viewDetails: 'Ver detalles', partner: 'Colaborador', partners: 'Colaboradores',
  },
  english: {
    draft: 'Draft', initiated: 'Initiated', processing: 'In Progress', completed: 'Completed',
    client: 'Client', lawyer: 'Lawyer', notAssigned: 'Not assigned',
    start: 'Start', end: 'End', created: 'Created', progress: 'Progress', fees: 'Fees',
    completedCase: 'Case completed', tasksCompleted: 'tasks completed',
    viewDetails: 'View details', partner: 'Partner', partners: 'Partners',
  }
};

const currencyFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

// Estado -> color de tokens (equivalente a .st.tram / .st.fin del prototipo)
const getStatusColor = (status) => {
  switch (status) {
    case 'draft': return tokens.colors.muted2;
    case 'initiated': return tokens.colors.indigo;
    case 'processing': return tokens.colors.gold;
    case 'completed': return tokens.colors.ok;
    default: return tokens.colors.indigo;
  }
};

const CaseCard = ({ expedient, onViewDetails, officeColor, userRoleInCase, showRoleBadge }) => {
  const t = translations[expedient.language] || translations.spanish;
  const statusColor = getStatusColor(expedient.state);

  const getStatusLabel = (status) => t[status] || status;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const locale = expedient.language === 'english' ? 'en-US' : 'es-ES';
    return new Date(dateString).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getMemberName = (member) => {
    if (!member) return t.notAssigned;
    if (member.person?.full_name) return member.person.full_name.replace(';', '');
    if (member.name_str) return member.name_str;
    return t.notAssigned;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.replace(';', '').split(' ').filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return parts[0]?.substring(0, 2).toUpperCase() || '?';
  };

  const progress = expedient.tasks?.length
    ? {
        percentage: Math.round((expedient.tasks.filter(x => x.completed).length / expedient.tasks.length) * 100),
        completed: expedient.tasks.filter(x => x.completed).length,
        total: expedient.tasks.length
      }
    : null;

  const MemberRow = ({ label, member, badge }) => {
    const name = getMemberName(member);
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar
          sx={{
            width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700,
            backgroundImage: gradientForName(name), color: '#0a0b10',
            border: `2px solid ${tokens.colors.panel}`,
            opacity: member?.person_id ? 1 : 0.75,
          }}
        >
          {getInitials(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: tokens.colors.muted, fontSize: '0.65rem', lineHeight: 1.2 }}>
            {label}{badge}
          </Typography>
          <Tooltip title={name} arrow placement="top" disableHoverListener={name.length < 25}>
            <Typography sx={{ fontWeight: 550, fontSize: '0.8rem', color: tokens.colors.txt, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  return (
    <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Card
        onClick={() => onViewDetails(expedient.id)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          borderRadius: `${tokens.radius.lg}px`,
          border: `1px solid ${tokens.colors.line}`,
          boxShadow: tokens.shadow.card,
          backgroundColor: tokens.colors.panel,
          transition: 'transform .2s, box-shadow .2s',
          '&:hover': {
            transform: 'translateY(-3px)',
            borderColor: tokens.colors.line2,
          },
        }}
      >
        <Box sx={{ height: 4, backgroundColor: statusColor }} />

        <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} gap={1}>
            <Tooltip title={expedient.title} arrow placement="top" enterDelay={300}>
              <Typography
                sx={{
                  fontWeight: 660, fontSize: '1rem', lineHeight: 1.3, color: tokens.colors.txt,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', minHeight: '2.6em', cursor: 'help', flex: 1,
                }}
              >
                {expedient.title}
              </Typography>
            </Tooltip>
            <Chip
              label={getStatusLabel(expedient.state)}
              size="small"
              icon={expedient.state === 'completed' ? <CheckIcon sx={{ fontSize: 16, color: '#fff !important' }} /> : undefined}
              sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24, backgroundColor: statusColor, color: '#fff', flexShrink: 0 }}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={2}>
            <Typography
              sx={{
                color: tokens.colors.muted, fontWeight: 600, fontSize: '0.7rem',
                backgroundColor: tokens.colors.panel2, px: 1, py: 0.3, borderRadius: `${tokens.radius.sm}px`,
              }}
            >
              {expedient.number}
            </Typography>
            {expedient.type && (
              <Typography sx={{ color: tokens.colors.muted, fontSize: '0.7rem' }}>• {expedient.type}</Typography>
            )}
          </Stack>

          {expedient.billed_amount != null && (
            <Box display="flex" alignItems="center" gap={0.5} mb={2}>
              <PaidIcon sx={{ fontSize: 14, color: tokens.colors.gold }} />
              <Typography sx={{ color: tokens.colors.muted, fontSize: '0.7rem' }}>
                {t.fees}: <b style={{ color: tokens.colors.txt }}>{currencyFormatter.format(expedient.billed_amount)}</b>
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 2, borderColor: tokens.colors.line }} />

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {expedient.members?.client && <MemberRow label={t.client} member={expedient.members.client} />}
            {expedient.members?.lawyer && <MemberRow label={t.lawyer} member={expedient.members.lawyer} />}
            {expedient.members?.partners?.length > 0 && (
              expedient.members.partners.length === 1
                ? <MemberRow label={t.partner} member={expedient.members.partners[0]} />
                : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{
                        width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700,
                        backgroundImage: gradientForName(getMemberName(expedient.members.partners[0])), color: '#0a0b10',
                        border: `2px solid ${tokens.colors.panel}`,
                      }}
                    >
                      {getInitials(getMemberName(expedient.members.partners[0]))}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: tokens.colors.muted, fontSize: '0.65rem' }}>
                        {t.partners} ({expedient.members.partners.length})
                      </Typography>
                      <Tooltip
                        title={expedient.members.partners.map(p => getMemberName(p)).join(', ')}
                        arrow placement="top"
                      >
                        <Typography sx={{ fontWeight: 550, fontSize: '0.8rem', color: tokens.colors.txt, cursor: 'help' }}>
                          {expedient.members.partners.slice(0, 2).map(p => getMemberName(p)).join(', ')}
                          {expedient.members.partners.length > 2 && ` +${expedient.members.partners.length - 2}`}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                )
            )}
          </Stack>

          {(expedient.start_date || expedient.end_date || expedient.created_at) && (
            <>
              <Divider sx={{ mb: 1.5, borderColor: tokens.colors.line }} />
              <Stack spacing={0.8}>
                {expedient.start_date && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarIcon sx={{ fontSize: 14, color: tokens.colors.ok }} />
                    <Typography sx={{ color: tokens.colors.muted, fontSize: '0.7rem' }}>
                      {t.start}: <b style={{ color: tokens.colors.txt }}>{formatDate(expedient.start_date)}</b>
                    </Typography>
                  </Box>
                )}
                {expedient.end_date && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarIcon sx={{ fontSize: 14, color: tokens.colors.red }} />
                    <Typography sx={{ color: tokens.colors.muted, fontSize: '0.7rem' }}>
                      {t.end}: <b style={{ color: tokens.colors.txt }}>{formatDate(expedient.end_date)}</b>
                    </Typography>
                  </Box>
                )}
                {!expedient.start_date && !expedient.end_date && expedient.created_at && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarIcon sx={{ fontSize: 14, color: tokens.colors.muted }} />
                    <Typography sx={{ color: tokens.colors.muted, fontSize: '0.7rem' }}>
                      {t.created}: <b style={{ color: tokens.colors.txt }}>{formatDate(expedient.created_at)}</b>
                    </Typography>
                  </Box>
                )}
              </Stack>
            </>
          )}

          {progress && expedient.state !== 'completed' && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <ProgressIcon sx={{ fontSize: 14, color: tokens.colors.indigo }} />
                  <Typography sx={{ color: tokens.colors.muted, fontWeight: 650, fontSize: '0.7rem' }}>{t.progress}</Typography>
                </Box>
                <Typography sx={{ color: tokens.colors.muted, fontWeight: 700, fontSize: '0.75rem' }}>
                  {progress.completed}/{progress.total} • {progress.percentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress.percentage}
                sx={{
                  height: 6, borderRadius: 3, backgroundColor: tokens.colors.panel2,
                  '& .MuiLinearProgress-bar': { backgroundImage: tokens.gradient.main, borderRadius: 3 },
                }}
              />
            </Box>
          )}

          {expedient.state === 'completed' && progress && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(31,157,99,.10)', borderRadius: `${tokens.radius.sm}px`, border: `1px solid rgba(31,157,99,.25)` }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckIcon sx={{ fontSize: 18, color: tokens.colors.ok }} />
                <Typography sx={{ color: tokens.colors.ok, fontWeight: 650, fontSize: '0.75rem' }}>
                  {t.completedCase} • {progress.total} {t.tasksCompleted}
                </Typography>
              </Box>
            </Box>
          )}

          {showRoleBadge && userRoleInCase && (
            <Chip
              label={t[userRoleInCase] || userRoleInCase}
              size="small"
              sx={{ mt: 2, backgroundImage: tokens.gradient.soft, color: tokens.colors.txt, border: `1px solid ${tokens.colors.line2}`, fontWeight: 600 }}
            />
          )}
        </CardContent>

        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            size="small"
            startIcon={<ViewIcon />}
            onClick={(e) => { e.stopPropagation(); onViewDetails(expedient.id); }}
            sx={{
              borderRadius: `${tokens.radius.md}px`,
              textTransform: 'none',
              fontWeight: 650,
              fontSize: '0.85rem',
              py: 1,
              border: `1px solid ${tokens.colors.line2}`,
              color: tokens.colors.txt,
              backgroundColor: tokens.colors.panel,
              '&:hover': { backgroundColor: tokens.colors.panel2, borderColor: officeColor || tokens.colors.indigo },
            }}
          >
            {t.viewDetails}
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};

export default CaseCard;